import { SelectionToolbar } from '@/components/selection-bar/SelectionToolbar'
import { ShortcutHint } from '@/components/ui/shortcut-hint'
import { TABLE_CELL_PADDING_CLASS, TABLE_HEADER_CELL_CLASS, tableRowHeightPx } from '@/components/ui/table-tokens'
import { useIsMobile } from '@/hooks/use-mobile'
import { useUIOverlayState } from '@/hooks/useIsDialogOpen'
import { useResizeObserver } from '@/hooks/useResizeObserver'
import { holdsKeyboardFocus, shouldSkipShortcut } from '@/lib/keyboard'
import { MODAL_CONTENT_SELECTOR, dismissOverlayLayers, isInOverlayLayer } from '@/lib/overlayLayers'
import { useShortcut } from '@/lib/useShortcut'
import { cn } from '@/lib/utils'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
	DndContext,
	DragOverlay,
	PointerSensor,
	TouchSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import type { Header, Row } from '@tanstack/react-table'
import { flexRender } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowDown, ArrowDownUp, ArrowUp, GripVertical } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { BOTTOM_BAR_COLLAPSE_EVENT, BOTTOM_BAR_ID, BOTTOM_BAR_OPEN_CLASS } from '../bottom-bar/constants'
import { ColumnHeaderLabel } from './ColumnHeaderLabel'
import { DraggableColumnHeader } from './DraggableColumnHeader'
import { TruncatedCell } from './TruncatedCell'
import { matchActiveRowKey } from './activeRowKeys'
import { getTableRowVariantClass } from './rowVariants'
import type { TableViewProps } from './types'

export interface ColumnMeta {
	alignment?: 'left' | 'center' | 'right'
	fixedWidth?: boolean
	disableTruncation?: boolean // If true, don't wrap cell content in TruncatedCell
	headerTooltip?: ReactNode // Tooltip shown on column header hover to describe the column
	label?: string // Plain text name of the column, used where a rendered header cannot be (accessible sort label)
}

// A column whose `header` is a component has no readable text, so fall back to the meta label, then the column id.
export const getColumnSortLabel = <TData,>(header: Header<TData, unknown>) => {
	const { header: headerDef, meta } = header.column.columnDef
	return typeof headerDef === 'string' ? headerDef : ((meta as ColumnMeta | undefined)?.label ?? header.column.id)
}

// A frozen (sticky) cell paints its own opaque backing instead of inheriting the row's, so it needs
// the row's selected/active state mirrored explicitly to avoid the highlight stopping at its edge.
const getFrozenCellClass = (isSelected: boolean, isActiveRow: boolean) => {
	if (isSelected) {
		return isActiveRow ? 'frozen-cell-selected-hover' : 'frozen-cell-selected'
	}
	return isActiveRow ? 'frozen-cell-hover' : 'frozen-cell-solid'
}

// Helper function to get CSS classes for alignment
export const getAlignmentClasses = (alignment: string) => {
	switch (alignment) {
		case 'center':
			return 'justify-center text-center'
		case 'right':
			return 'justify-end text-right'
		case 'left':
			return 'justify-start text-left'
		default:
			return 'justify-start text-left'
	}
}

/** Controls that act on Space themselves. Not [tabindex]: a focused container must not swallow it. */
const SPACE_CONTROL_SELECTOR =
	'button, input, summary, [role="button"], [role="checkbox"], [role="switch"], [role="tab"], [role="radio"]'

export function TableView<TData>({
	table,
	getRowId,
	frozenColumnIds,
	frozenRightColumnIds,
	staticColumnIds,
	enableSelection = false,
	selectionState,
	selectionToolbarProps,
	enableColumnReordering = false,
	onColumnReorder,
	onRowClick,
	onRowDoubleClick,
	onRowRightClick,
	onRowKeySelect,
	onGroupRowKeySelect,
	onHoveredRowChange,
	highlightedRowId,
	getRowVariant,
	getRowClassName,
	enableGrouping = false,
	preserveSelectionToolbarInBottomBar = false,
	onScroll,
	dataTestId,
}: TableViewProps<TData>) {
	const tableContainerRef = useRef<HTMLDivElement>(null)
	const headerScrollRef = useRef<HTMLDivElement>(null)
	const bodyRef = useRef<HTMLDivElement>(null)
	const headerContentRef = useRef<HTMLDivElement>(null)
	const hoveredGroupRowRef = useRef<Row<TData> | null>(null)
	const [scrollbarWidth, setScrollbarWidth] = useState(0)
	const [containerWidth, setContainerWidth] = useState(0)
	// drag state management
	const [activeColumnId, setActiveColumnId] = useState<string | null>(null)
	const [isDragActive, setIsDragActive] = useState(false)
	// touch detection
	const [isTouchDevice, setIsTouchDevice] = useState(false)
	const [headerHovered, setHeaderHovered] = useState(false)
	// Track UI overlay states (dialog and bottom bar) to hide selection toolbar
	// The ref narrows this to dialogs the table is not inside: a table rendered in a dialog keeps its
	// own selection toolbar, while a page table still hides it under a dialog opened above it.
	const { isDialogOpen, isBottomBarOpen } = useUIOverlayState(tableContainerRef)
	// Measured height of the scroll body (for needsVerticalScroll calculation)
	const [bodyHeight, setBodyHeight] = useState(400)

	const isMobile = useIsMobile()

	useEffect(() => {
		setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0)
	}, [])

	// Measure scroll body height for vertical scrollbar / column sizing
	useResizeObserver(bodyRef, () => setBodyHeight(bodyRef.current?.clientHeight ?? 0))

	const rows = table.getRowModel().rows
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const headers = table.getHeaderGroups()[0]?.headers || []

	// Helper function to check if header has fixed width - moved outside useMemo to avoid re-render issues
	const staticColumnSet = useMemo(() => new Set(staticColumnIds ?? []), [staticColumnIds])
	const hasFixedWidth = useCallback(
		(header: Header<TData, unknown>) => {
			const meta = header.column.columnDef.meta as ColumnMeta | undefined
			return meta?.fixedWidth === true || staticColumnSet.has(header.column.id)
		},
		[staticColumnSet],
	)

	// Configurable frozen columns or default to first 2 columns
	const finalFrozenColumnIds = useMemo(() => {
		if (frozenColumnIds) {
			const currentColumnIdSet = new Set(headers.map(h => h.column.id))
			return frozenColumnIds.filter(id => currentColumnIdSet.has(id))
		}
		const defaultFrozenCount = isMobile ? 1 : 2
		return headers.slice(0, defaultFrozenCount).map(h => h.column.id)
	}, [frozenColumnIds, headers, isMobile])

	const frozenColumnSet = useMemo(() => new Set(finalFrozenColumnIds), [finalFrozenColumnIds])

	// Right-side frozen columns
	const finalFrozenRightColumnIds = useMemo(() => {
		if (frozenRightColumnIds) {
			const currentColumnIdSet = new Set(headers.map(h => h.column.id))
			return frozenRightColumnIds.filter(id => currentColumnIdSet.has(id))
		}
		return []
	}, [frozenRightColumnIds, headers])

	const frozenRightColumnSet = useMemo(() => new Set(finalFrozenRightColumnIds), [finalFrozenRightColumnIds])

	// Only exclude frozen columns from draggable list
	const draggableColumnSet = useMemo(() => {
		const draggableIds = headers
			.filter(header => !frozenColumnSet.has(header.column.id) && !frozenRightColumnSet.has(header.column.id))
			.map(header => header.column.id)

		return new Set(draggableIds)
	}, [headers, frozenColumnSet, frozenRightColumnSet])

	// ALL column IDs for SortableContext (includes frozen columns but they won't be draggable)
	const allColumnIds = useMemo(() => {
		return headers.map(header => header.column.id)
	}, [headers])

	/**
	 * Calculates the left position offset for sticky/frozen columns.
	 */
	const getFrozenLeftOffset = useCallback(
		(columnIndex: number, columnWidths: number[]) => {
			return columnWidths.slice(0, columnIndex).reduce((acc, width, i) => {
				return frozenColumnSet.has(headers[i]?.column.id || '') ? acc + width : acc
			}, 0)
		},
		[frozenColumnSet, headers],
	)

	/**
	 * Calculates the right position offset for right-side sticky/frozen columns.
	 */
	const getFrozenRightOffset = useCallback(
		(columnIndex: number, columnWidths: number[]) => {
			return columnWidths.slice(columnIndex + 1).reduce((acc, width, i) => {
				const actualIndex = columnIndex + 1 + i
				return frozenRightColumnSet.has(headers[actualIndex]?.column.id || '') ? acc + width : acc
			}, 0)
		},
		[frozenRightColumnSet, headers],
	)

	// Calculate scrollbar width for perfect header-body alignment
	useEffect(() => {
		const calculateScrollbarWidth = () => {
			const outer = document.createElement('div')
			outer.style.visibility = 'hidden'
			outer.style.overflow = 'scroll'
			outer.style.width = '100px'
			outer.style.height = '100px'

			outer.style.setProperty('-ms-overflow-style', 'scrollbar')
			document.body.appendChild(outer)

			const inner = document.createElement('div')
			inner.style.width = '100%'
			inner.style.height = '100%'
			outer.appendChild(inner)

			const scrollbarWidth = outer.offsetWidth - inner.offsetWidth
			document.body.removeChild(outer)

			return scrollbarWidth
		}

		setScrollbarWidth(calculateScrollbarWidth())
	}, [])

	// offsetWidth, not contentRect: the column maths works against the border box.
	useResizeObserver(tableContainerRef, () => setContainerWidth(tableContainerRef.current?.offsetWidth ?? 0))

	const rowHeight = tableRowHeightPx()
	const virtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => bodyRef.current,
		// The virtualizer positions rows by absolute pixel offsets, so this overrides any CSS row height.
		estimateSize: () => rowHeight,
		overscan: 5,
		// Tell the virtualizer the body height up front. On a fresh mount it hasn't
		// measured the body yet, so without this it assumes height 0 and draws only a
		// few rows for one frame before filling the page — a visible flash.
		initialRect: { width: containerWidth, height: bodyHeight },
		getItemKey: getRowId
			? index => {
					const row = rows[index]
					return getRowId(row.original)
				}
			: undefined,
	})

	const virtualItems = virtualizer.getVirtualItems()

	// Check if a row is selected or highlighted
	const isRowSelected = useCallback(
		(row: Row<TData>): boolean => {
			// For highlighting, check both TanStack row.id and computed rowId
			// This handles both group header rows and leaf rows
			if (highlightedRowId) {
				// Check TanStack's internal row ID (for group rows)
				if (row.id === highlightedRowId) {
					return true
				}
				// Check computed rowId from getRowId (for leaf rows)
				const rowId = getRowId ? getRowId(row.original) : row.id
				if (rowId && rowId === highlightedRowId) {
					return true
				}
			}
			// Check standard selection state
			if (!enableSelection || !selectionState) {
				return false
			}
			const rowId = getRowId ? getRowId(row.original) : row.id
			return selectionState.isSelected(rowId)
		},
		[enableSelection, selectionState, getRowId, highlightedRowId],
	)

	// Bidirectional scroll synchronization
	const syncScroll = useCallback((source: 'header' | 'body', scrollLeft: number) => {
		if (source === 'body' && headerScrollRef.current) {
			headerScrollRef.current.scrollLeft = scrollLeft
		} else if (source === 'header' && bodyRef.current) {
			bodyRef.current.scrollLeft = scrollLeft
		}
	}, [])

	// Scroll event listeners for real-time sync
	useEffect(() => {
		const headerElement = headerScrollRef.current
		const bodyElement = bodyRef.current

		if (!headerElement || !bodyElement) {
			return
		}

		const handleHeaderScroll = () => {
			syncScroll('header', headerElement.scrollLeft)
		}

		const handleBodyScroll = () => {
			syncScroll('body', bodyElement.scrollLeft)
		}

		headerElement.addEventListener('scroll', handleHeaderScroll)
		bodyElement.addEventListener('scroll', handleBodyScroll)

		return () => {
			headerElement.removeEventListener('scroll', handleHeaderScroll)
			bodyElement.removeEventListener('scroll', handleBodyScroll)
		}
	}, [syncScroll])

	// Base column width calculations - now properly includes hasFixedWidth in dependencies
	const { baseColumnWidths, baseTotalWidth } = useMemo(() => {
		const baseColumnWidths = headers.map(header => {
			const originalSize = header.getSize()

			if (header.id === 'select') {
				return 30
			}
			if (isMobile) {
				if (hasFixedWidth(header)) {
					return originalSize
				}
				const minMobileWidth = 120
				return Math.max(originalSize, minMobileWidth)
			}
			return originalSize
		})

		const baseTotalWidth = baseColumnWidths.reduce((sum, width) => sum + width, 0)

		return {
			baseColumnWidths,
			baseTotalWidth,
		}
	}, [headers, isMobile, hasFixedWidth])

	// Dynamic column sizing calculations - prevents fixed columns from being expanded.
	// Expands non-fixed columns only when there is extra available width and no horizontal scroll is needed.
	const { columnWidths, totalWidth, shouldExpandColumns, needsVerticalScroll, contentHeight } = useMemo(() => {
		const contentHeight = virtualizer.getTotalSize()
		const needsVerticalScroll = contentHeight > bodyHeight

		const availableWidth = containerWidth - (needsVerticalScroll ? scrollbarWidth : 0)
		const needsHorizontalScroll = baseTotalWidth > availableWidth

		const shouldExpandColumns =
			baseTotalWidth < availableWidth && availableWidth > 0 && !needsHorizontalScroll && !isMobile

		let columnWidths = baseColumnWidths
		let totalWidth = baseTotalWidth

		if (shouldExpandColumns) {
			const extraWidth = availableWidth - baseTotalWidth

			// Get expandable columns (non-fixed, non-select)
			const expandableColumns = headers.filter(h => h.id !== 'select' && !hasFixedWidth(h))
			const expandableColumnsCount = expandableColumns.length

			// Only distribute extra space among expandable columns
			const extraPerColumn = expandableColumnsCount > 0 ? Math.floor(extraWidth / expandableColumnsCount) : 0

			columnWidths = baseColumnWidths.map((width, index) => {
				const header = headers[index]
				// Keep fixed-width columns and select column at their original size
				if (header.id === 'select' || hasFixedWidth(header)) {
					return width
				}
				// Expand only non-fixed columns
				return width + extraPerColumn
			})

			totalWidth = columnWidths.reduce((sum, width) => sum + width, 0)
		}

		return {
			columnWidths,
			totalWidth,
			shouldExpandColumns,
			needsVerticalScroll,
			contentHeight,
		}
	}, [
		baseColumnWidths,
		baseTotalWidth,
		headers,
		containerWidth,
		scrollbarWidth,
		bodyHeight,
		virtualizer,
		isMobile,
		hasFixedWidth,
	])
	const viewportWidth = containerWidth - (needsVerticalScroll ? scrollbarWidth : 0)

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: isMobile ? 12 : 8,
			},
		}),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: isMobile ? 500 : 300,
				tolerance: isMobile ? 15 : 10,
			},
		}),
	)

	const cleanupDragState = useCallback(() => {
		setActiveColumnId(null)
		setIsDragActive(false)
		document.body.classList.remove('is-dragging')

		if (isTouchDevice && tableContainerRef.current) {
			tableContainerRef.current.style.touchAction = ''
		}
	}, [isTouchDevice])

	const handleDragStart = useCallback(
		(event: DragStartEvent) => {
			const { active } = event

			if (!draggableColumnSet.has(String(active.id))) {
				return
			}

			setActiveColumnId(String(active.id))
			setIsDragActive(true)
			document.body.classList.add('is-dragging')

			if (isTouchDevice && tableContainerRef.current) {
				tableContainerRef.current.style.touchAction = 'none'
			}
		},
		[draggableColumnSet, isTouchDevice],
	)

	const handleDragCancel = useCallback(() => {
		cleanupDragState()
	}, [cleanupDragState])

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event

			try {
				if (!over) {
					return
				}
				const activeId = String(active.id)
				const overId = String(over.id)

				if (activeId === overId) {
					return
				}
				if (frozenColumnSet.has(overId) || frozenRightColumnSet.has(overId)) {
					return
				}
				if (!draggableColumnSet.has(activeId) || !draggableColumnSet.has(overId)) {
					return
				}
				if (enableColumnReordering && onColumnReorder) {
					onColumnReorder(activeId, overId)
				}
			} finally {
				cleanupDragState()
			}
		},
		[
			frozenColumnSet,
			frozenRightColumnSet,
			draggableColumnSet,
			enableColumnReordering,
			onColumnReorder,
			cleanupDragState,
		],
	)

	const [fixedToolbarPosition, setFixedToolbarPosition] = useState<{ x: number; y: number } | null>(null)

	/**
	 * A table inside a dialog portals its toolbar into that dialog, not the body: the body sits
	 * outside the modal, where Radix marks the rest of the page aria-hidden and a pointer down
	 * counts as a click outside the dialog.
	 */
	const toolbarPortalHost = tableContainerRef.current?.closest<HTMLElement>(MODAL_CONTENT_SELECTOR) ?? null
	/**
	 * `fixedToolbarPosition` is in viewport coordinates, but the dialog carries a transform, which
	 * makes it the containing block for the `fixed` toolbar inside it. Subtracting the host's own
	 * origin puts the toolbar back where the anchor actually is.
	 */
	const toolbarPortalOrigin = toolbarPortalHost
		? { x: toolbarPortalHost.getBoundingClientRect().left, y: toolbarPortalHost.getBoundingClientRect().top }
		: { x: 0, y: 0 }

	const getAnchorPosition = useCallback(
		(options: { type: 'header' } | { type: 'row'; rowId: string }): { x: number; y: number } | null => {
			const anchorColumnId = selectionToolbarProps?.anchorColumnId
			const anchorToFirstColumn = selectionToolbarProps?.anchorToFirstColumn
			const targetColumnId =
				anchorColumnId || (anchorToFirstColumn ? headers[0]?.id : headers.find(h => h.id !== 'select')?.id)

			if (!targetColumnId) {
				return null
			}

			if (options.type === 'header') {
				if (!headerScrollRef.current) {
					return null
				}
				const columnElement = headerScrollRef.current.querySelector(`[data-column-id="${targetColumnId}"]`)
				if (columnElement) {
					const rect = columnElement.getBoundingClientRect()
					return {
						x: rect.right + 10,
						y: rect.top + rect.height / 2 - 20, // Center vertically with toolbar height ~40px
					}
				}
				return null
			}

			// Row anchor positioning
			const { rowId } = options
			if (!bodyRef.current) {
				return null
			}

			const columnElement = bodyRef.current.querySelector(
				`[data-row-id="${rowId}"] [data-column-id="${targetColumnId}"]`,
			)

			if (!columnElement) {
				return null
			}

			const rect = columnElement.getBoundingClientRect()
			return {
				x: rect.right + 10,
				y: rect.top + rect.height / 2 - 20, // Center vertically with toolbar height ~40px
			}
		},
		[headers, selectionToolbarProps?.anchorColumnId, selectionToolbarProps?.anchorToFirstColumn],
	)

	// useEffect to capture and maintain the toolbar position based on anchor geometry
	useEffect(() => {
		if (!selectionState || selectionState.selectedCount === 0) {
			setFixedToolbarPosition(null)
			return
		}

		// Function to get anchor position with fallback to header if row is not present
		const resolvePosition = () => {
			// If all rows are selected, we always anchor to the header
			if (selectionState.isAllSelected) {
				return getAnchorPosition({ type: 'header' })
			}

			// Try to anchor to the last interaction point (last checked row)
			const anchorRowId =
				selectionState.lastCheckedRowId ||
				(selectionState.selectedRowIds.length > 0
					? selectionState.selectedRowIds[selectionState.selectedRowIds.length - 1]
					: null)

			if (anchorRowId) {
				const rowPos = getAnchorPosition({ type: 'row', rowId: anchorRowId })
				if (rowPos) {
					return rowPos
				}
			}

			// Fallback to header position if the anchor row is not present on the current page
			// (e.g. across pagination or after filtering)
			return getAnchorPosition({ type: 'header' })
		}

		setFixedToolbarPosition(resolvePosition())
	}, [selectionState, getAnchorPosition])

	const [hoveredRow, setHoveredRow] = useState<TData | null>(null)

	useEffect(() => {
		onHoveredRowChange?.(hoveredRow)
	}, [hoveredRow, onHoveredRowChange])

	// The one active row J/K/arrows/Ctrl+U/Ctrl+D move, and a mouse hover quietly moves too (no focus
	// ring) — a keyboard move picks up from wherever the mouse left it rather than the top.
	const [activeRowIndex, setActiveRowIndex] = useState(-1)
	const activeRowMovedByKeyboardRef = useRef(false)
	// Clearing the highlight on mouse-leave (activeRowIndex -> -1) must not also forget *where* to
	// resume: unlike activeRowIndex, this never resets to -1 on its own, so the first keyboard move
	// after the mouse simply leaves (never touching the keyboard) still picks up from that row rather
	// than restarting at the top — the one thing this component's own hover/keyboard handoff promises.
	const lastRowIndexRef = useRef(-1)
	// The row index Shift+Arrow is extending from. Set on the first Shift+Arrow of a run, cleared by any
	// other move (keyboard or mouse) so the next Shift+Arrow run starts fresh from wherever the cursor is.
	const shiftExtendAnchorRef = useRef<number | null>(null)
	const activeRow = activeRowIndex >= 0 && activeRowIndex < rows.length ? rows[activeRowIndex] : undefined

	// A keyboard move can leave the mouse resting over whatever row it started from, so the active row
	// (moved by either input) wins over the merely hovered one once they diverge.
	const activeOrHoveredRow = activeRow?.original ?? hoveredRow
	// The hint shows on this one row alone — never on both a literally-hovered row and a separate
	// keyboard-focused row at once, even though CSS :hover and :focus can be true on two different rows
	// at the same time.
	const activeOrHoveredRowKey = activeOrHoveredRow && getRowId ? getRowId(activeOrHoveredRow) : undefined

	useEffect(() => {
		if (!activeRowMovedByKeyboardRef.current || !activeRow) {
			return
		}
		const rowKey = String(getRowId ? getRowId(activeRow.original) : activeRow.id)
		const focusRow = () => {
			const candidates = tableContainerRef.current?.querySelectorAll<HTMLElement>('[data-active-row-id]')
			for (const element of candidates ?? []) {
				if (element.getAttribute('data-active-row-id') === rowKey) {
					element.focus()
					return
				}
			}
		}
		focusRow()
		// A jump to the first/last row can land outside the virtualizer's overscan window, so the row
		// may not exist yet on this paint — one more try next frame covers that without polling forever.
		const frame = requestAnimationFrame(focusRow)
		return () => cancelAnimationFrame(frame)
	}, [activeRow, getRowId])

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			const match = matchActiveRowKey(event)
			if (!match) {
				return
			}
			if (shouldSkipShortcut(event) || isInOverlayLayer(event.target)) {
				return
			}
			if (rows.length === 0) {
				return
			}
			// activeRowIndex itself may read -1 right now (the mouse left with no keyboard move since,
			// clearing only the visual highlight) while lastRowIndexRef still remembers where it was.
			const previousIndex = activeRowIndex >= 0 ? activeRowIndex : lastRowIndexRef.current
			const nextIndex =
				match.move === 'first'
					? 0
					: match.move === 'last'
						? rows.length - 1
						: match.move === 'next'
							? Math.min(previousIndex + 1, rows.length - 1)
							: Math.max(previousIndex - 1, 0)
			event.preventDefault()
			activeRowMovedByKeyboardRef.current = true
			virtualizer.scrollToIndex(nextIndex, { align: 'auto' })
			lastRowIndexRef.current = nextIndex
			setActiveRowIndex(nextIndex)
			// Hover follows the keyboard move too, so a row hover drives (X, the device action sequence's
			// target row, …) sees the same row a mouse hovering it would — not just the active-row ring.
			const nextRow = rows[nextIndex]
			if (enableGrouping && nextRow.getIsGrouped?.()) {
				hoveredGroupRowRef.current = nextRow
				setHoveredRow(null)
			} else {
				hoveredGroupRowRef.current = null
				setHoveredRow(nextRow.original)
			}

			if (match.extendSelection && enableSelection && selectionState) {
				// First Shift+Arrow of a run: anchor where the cursor already was (or where it just landed,
				// if nothing was active yet, so that first press selects just the one row).
				const anchor = shiftExtendAnchorRef.current ?? (previousIndex >= 0 ? previousIndex : nextIndex)
				shiftExtendAnchorRef.current = anchor
				// Clear the previous step's span before laying down the new one — select/deselect both no-op
				// on a row already in the right state, so redoing the whole span each step is cheap and, unlike
				// patching just the diff, never leaves a stale row selected when the run reverses direction.
				if (previousIndex >= 0) {
					selectionState.rangeSelect(Math.min(anchor, previousIndex), Math.max(anchor, previousIndex), false)
				}
				selectionState.rangeSelect(Math.min(anchor, nextIndex), Math.max(anchor, nextIndex), true)
			} else {
				shiftExtendAnchorRef.current = null
			}
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [rows, activeRowIndex, virtualizer, enableGrouping, enableSelection, selectionState])

	const onToggleHoveredRow = useCallback(
		(event: KeyboardEvent) => {
			if (activeOrHoveredRow) {
				if (enableSelection && selectionState && getRowId) {
					event.preventDefault()
					// Respect selectableRowFilter — rows with a disabled checkbox must not be
					// selectable via the keyboard either.
					if (selectionState.isRowSelectable(activeOrHoveredRow)) {
						selectionState.toggleRow(getRowId(activeOrHoveredRow), activeOrHoveredRow)
					}
				} else if (onRowKeySelect) {
					event.preventDefault()
					onRowKeySelect(activeOrHoveredRow)
				}
			} else if (hoveredGroupRowRef.current && onGroupRowKeySelect) {
				event.preventDefault()
				const leafRows = hoveredGroupRowRef.current.getLeafRows().map(r => r.original)
				onGroupRowKeySelect(leafRows)
			}
		},
		[activeOrHoveredRow, enableSelection, selectionState, getRowId, onRowKeySelect, onGroupRowKeySelect],
	)
	// Menus and dialogs keep their own keys, so this skips while the target is inside one.
	useShortcut('table.toggleHoveredRow', onToggleHoveredRow, { capture: true, skipInOverlay: true })

	// Space activates these, so the shortcut must not take the key from a keyboard user.
	const takesSpace = useCallback(
		(target: EventTarget | null) => target instanceof HTMLElement && target.closest(SPACE_CONTROL_SELECTOR) !== null,
		[],
	)
	const isKeyboardActivated = useCallback(
		(target: EventTarget | null) =>
			target instanceof HTMLElement &&
			target.closest(`${SPACE_CONTROL_SELECTOR}, a[href], [tabindex]:not([tabindex="-1"])`) !== null,
		[],
	)
	const onOpenHoveredRow = useCallback(
		(event: KeyboardEvent) => {
			// Check if the bottom bar is currently open (full-page).
			// Read DOM directly for a synchronous, always-fresh check.
			const bottomBar = document.getElementById(BOTTOM_BAR_ID)
			const isBarCurrentlyOpen = bottomBar?.classList.contains(BOTTOM_BAR_OPEN_CLASS) ?? false

			if (isBarCurrentlyOpen) {
				// A control in the bar holding keyboard focus keeps Space for itself. Inside an open menu it does
				// not: Radix would select an item and leave the menu portaled over the collapsed bar.
				if (!isInOverlayLayer(event.target) && isKeyboardActivated(event.target) && holdsKeyboardFocus(event.target)) {
					return
				}

				// Collapse the bar — no hoveredRow needed since the full-page bar
				// covers the table, clearing hoveredRow via onMouseLeave.
				// stopPropagation: Radix selects a menu item on Space from its own React handler, which
				// would otherwise leave that menu floating over the collapsed bar.
				event.preventDefault()
				event.stopPropagation()
				dismissOverlayLayers()
				window.dispatchEvent(new CustomEvent(BOTTOM_BAR_COLLAPSE_EVENT))
				return
			}

			// Bar is closed: open the active row. Only a control holding keyboard focus keeps Space for itself.
			if (
				activeOrHoveredRow &&
				onRowDoubleClick &&
				!isInOverlayLayer(event.target) &&
				!(takesSpace(event.target) && holdsKeyboardFocus(event.target))
			) {
				event.preventDefault()
				// A checkbox a click left focused would tick on this Space as well.
				event.stopPropagation()
				onRowDoubleClick(activeOrHoveredRow)
			}
		},
		[activeOrHoveredRow, onRowDoubleClick, isKeyboardActivated, takesSpace],
	)
	// Capture: Radix's own Space handling runs on the bubble phase, so the bar has to claim the key
	// before it to collapse instead of opening a menu. Not skipInOverlay: an open menu still collapses
	// the bar below it, it just keeps Space for itself when the bar is already closed.
	useShortcut('table.openHoveredRow', onOpenHoveredRow, { capture: true })

	// Enter is the browser's own "activate what's focused" convention, so unlike Space it only opens a
	// row that genuinely holds keyboard focus — never a merely hovered one. Skipped when the page also
	// wires onRowClick: that row already handles Enter itself, to a different action, in its own
	// onKeyDown below, and firing both here would run two actions off one keypress.
	const onOpenActiveRow = useCallback(
		(event: KeyboardEvent) => {
			if (onRowClick || !activeRow || !onRowDoubleClick) {
				return
			}
			const rowKey = String(getRowId ? getRowId(activeRow.original) : activeRow.id)
			if (!(event.target instanceof HTMLElement) || event.target.getAttribute('data-active-row-id') !== rowKey) {
				return
			}
			event.preventDefault()
			onRowDoubleClick(activeRow.original)
		},
		[onRowClick, activeRow, getRowId, onRowDoubleClick],
	)
	useShortcut('table.openActiveRow', onOpenActiveRow, { skipInOverlay: true })

	// Escape closes one layer per press. Tooltips, menus and dialogs already close themselves (Radix);
	// this adds the next two: the selection, then the active row, in that order, before an outer Escape
	// handler (e.g. the bottom bar) ever sees a press this table hasn't already spent.
	const onEscapeClearActiveOrSelection = useCallback(
		(event: KeyboardEvent) => {
			if (enableSelection && selectionState && selectionState.selectedCount > 0) {
				event.preventDefault()
				event.stopPropagation()
				selectionState.deselectAll()
				return
			}
			if (activeRowIndex < 0) {
				return
			}
			event.preventDefault()
			event.stopPropagation()
			activeRowMovedByKeyboardRef.current = false
			shiftExtendAnchorRef.current = null
			setActiveRowIndex(-1)
			hoveredGroupRowRef.current = null
			setHoveredRow(null)
			const active = document.activeElement
			if (active instanceof HTMLElement && tableContainerRef.current?.contains(active)) {
				active.blur()
			}
		},
		[enableSelection, selectionState, activeRowIndex],
	)
	useShortcut('table.clearActiveOrSelection', onEscapeClearActiveOrSelection, { skipInOverlay: true })

	const headerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (headerRef.current) {
			const height = headerRef.current.offsetHeight
			tableContainerRef.current?.style.setProperty('--table-header-height', `${height}px`)
		}
	}, [])

	const tableContent = (
		<div
			ref={tableContainerRef}
			data-table-container='true'
			className='size-full flex flex-col min-w-0 bg-background'
			style={{ '--table-header-height': '40px' } as React.CSSProperties}>
			<div ref={headerRef} className='shrink-0 border-b bg-table-header-bg group'>
				<div
					ref={headerScrollRef}
					data-selection-safe-zone='true'
					className='overflow-x-auto scrollbar-hide text-body'
					style={{
						overflowY: 'hidden',
						scrollbarWidth: 'none',
						msOverflowStyle: 'none',
						paddingRight: needsVerticalScroll ? `${scrollbarWidth}px` : '0px',
						width: '100%',
					}}>
					<div
						ref={headerContentRef}
						onMouseEnter={() => setHeaderHovered(true)}
						onMouseLeave={() => setHeaderHovered(false)}
						className='flex items-center text-body'
						style={{
							width: shouldExpandColumns ? '100%' : `${totalWidth}px`,
							minWidth: '100%',
						}}>
						{enableColumnReordering ? (
							<SortableContext items={allColumnIds} strategy={horizontalListSortingStrategy}>
								{headers.map((header, index) => {
									const isFrozenLeft = frozenColumnSet.has(header.column.id)
									const isFrozenRight = frozenRightColumnSet.has(header.column.id)
									const isFrozen = isFrozenLeft || isFrozenRight
									const isDraggable = enableColumnReordering && !isFrozen
									const leftOffset = getFrozenLeftOffset(index, columnWidths)
									const rightOffset = getFrozenRightOffset(index, columnWidths)
									const alignment = (header.column.columnDef.meta as ColumnMeta | undefined)?.alignment || 'left'
									// Check if this is the first right-frozen column to add margin-left: auto
									const isFirstRightFrozen =
										isFrozenRight && (index === 0 || !frozenRightColumnSet.has(headers[index - 1]?.column.id))

									return (
										<DraggableColumnHeader
											key={`${header.column.id}-${header.depth}`}
											header={header}
											columnWidth={columnWidths[index]}
											isFrozen={isFrozen}
											isFrozenRight={isFrozenRight}
											leftOffset={leftOffset}
											rightOffset={rightOffset}
											isDraggable={isDraggable}
											isTouchDevice={isTouchDevice}
											alignment={alignment}
											isFirstRightFrozen={isFirstRightFrozen}
											isLastColumn={index === headers.length - 1}
											dataTestId={dataTestId}
										/>
									)
								})}
							</SortableContext>
						) : (
							headers.map((header, index) => {
								const isFrozenLeft = frozenColumnSet.has(header.column.id)
								const isFrozenRight = frozenRightColumnSet.has(header.column.id)
								const isFrozen = isFrozenLeft || isFrozenRight
								const leftOffset = getFrozenLeftOffset(index, columnWidths)
								const rightOffset = getFrozenRightOffset(index, columnWidths)
								const alignment = (header.column.columnDef.meta as ColumnMeta | undefined)?.alignment || 'left'
								const alignmentClasses = getAlignmentClasses(alignment)
								// Check if this is the first right-frozen column to add margin-left: auto
								const isFirstRightFrozen =
									isFrozenRight && (index === 0 || !frozenRightColumnSet.has(headers[index - 1]?.column.id))

								const canSort = header.column.getCanSort()
								const sortDirection = header.column.getIsSorted()
								const isSelectColumn = header.column.id === 'select'
								const handleSortClick = (event: React.MouseEvent) => {
									event.preventDefault()
									event.stopPropagation()

									if (canSort && !isSelectColumn) {
										header.column.toggleSorting()
									}
								}

								const handleSortKeyDown = (event: React.KeyboardEvent) => {
									if (event.key === 'Enter' || event.key === ' ') {
										event.preventDefault()
										event.stopPropagation()

										if (canSort && !isSelectColumn) {
											header.column.toggleSorting()
										}
									}
								}

								const showActiveSort = canSort && !isSelectColumn && sortDirection
								const showHoverSort = canSort && !isSelectColumn && !sortDirection

								const ActiveSortIcon = sortDirection === 'asc' ? ArrowUp : ArrowDown

								return (
									<div
										key={header.id}
										data-column-id={header.column.id}
										className={cn(
											'group relative flex items-center py-2 shrink-0 z-10',
											TABLE_HEADER_CELL_CLASS,
											isSelectColumn ? 'px-2 justify-center' : TABLE_CELL_PADDING_CLASS,
											alignmentClasses,
											isFrozen ? 'frozen-header-solid' : 'bg-table-header-bg',
											index === headers.length - 1 && 'flex-1',
										)}
										style={{
											width: `${columnWidths[index]}px`,
											...(isFirstRightFrozen && {
												marginLeft: 'auto',
											}),
											...(isFrozenLeft && {
												position: 'sticky',
												left: `${leftOffset}px`,
												zIndex: 20,
											}),
											...(isFrozenRight && {
												position: 'sticky',
												right: `${rightOffset}px`,
												zIndex: 20,
											}),
										}}>
										<div
											className={`flex-1 min-w-0 flex items-center ${alignmentClasses} transition-colors ${
												canSort && !isSelectColumn
													? 'cursor-pointer focus:outline-none focus:bg-accent/20 focus:ring-2 focus:ring-primary/20'
													: ''
											}`}
											onClick={handleSortClick}
											onKeyDown={handleSortKeyDown}
											tabIndex={canSort && !isSelectColumn ? 0 : -1}
											role={canSort && !isSelectColumn ? 'button' : undefined}
											aria-label={canSort && !isSelectColumn ? `Sort by ${getColumnSortLabel(header)}` : undefined}
											data-testid={dataTestId ? `${dataTestId}-header-${header.column.id}` : undefined}>
											{header.isPlaceholder ? null : (
												<ColumnHeaderLabel
													header={header}
													align={alignment as 'left' | 'center' | 'right'}
													dataTestId={dataTestId}
												/>
											)}
										</div>

										<div
											className='absolute right-1 top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center cursor-pointer'
											style={{ pointerEvents: canSort && !isSelectColumn ? 'auto' : 'none' }}
											onClick={handleSortClick}
											onKeyDown={handleSortKeyDown}
											tabIndex={canSort && !isSelectColumn ? 0 : -1}
											role={canSort && !isSelectColumn ? 'button' : undefined}
											aria-label={canSort && !isSelectColumn ? `Sort by ${getColumnSortLabel(header)}` : undefined}>
											{showActiveSort && (
												<ActiveSortIcon
													className='size-(--icon-standalone) text-foreground'
													style={{ pointerEvents: 'none' }}
												/>
											)}

											{showHoverSort && (
												<ArrowDownUp
													className='size-(--icon-standalone) text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity'
													style={{ pointerEvents: 'none' }}
												/>
											)}
										</div>
									</div>
								)
							})
						)}
					</div>
				</div>
			</div>

			<div
				ref={bodyRef}
				className='flex-1 overflow-auto scrollbar-thin min-w-0 bg-card min-h-0 pb-24 select-none'
				onScroll={onScroll}>
				<div
					style={{
						height: contentHeight,
						minHeight: '100%',
						position: 'relative',
						width: shouldExpandColumns ? '100%' : `${totalWidth}px`,
						minWidth: '100%',
					}}>
					{virtualItems.map(virtualItem => {
						const row = rows[virtualItem.index]
						const rowKey = getRowId ? getRowId(row.original) : row.id
						const isSelected = isRowSelected(row)
						const isActiveRow = rowKey === activeOrHoveredRowKey
						const cells = row.getVisibleCells()
						const rowVariant = getRowVariant ? getRowVariant(row.original) : undefined
						const customRowClass = getRowClassName ? getRowClassName(row.original) : undefined
						const variantClass = getTableRowVariantClass(rowVariant)

						return (
							<div key={row.id} className='relative' data-row-id={rowKey} data-row-variant={rowVariant}>
								<div
									data-testid={dataTestId ? `${dataTestId}-row-${rowKey}` : undefined}
									data-active-row-id={rowKey}
									tabIndex={-1}
									style={{
										position: 'absolute',
										top: 0,
										left: 0,
										width: shouldExpandColumns ? '100%' : `${totalWidth}px`,
										height: virtualItem.size,
										transform: `translateY(${virtualItem.start}px)`,
									}}
									className={cn(
										// Driven by the one active-row value (not a bare CSS :hover), so a mouse resting
										// on a stale row while the keyboard moves elsewhere never lights up two rows.
										'flex group transition-colors cursor-pointer bg-table-row-bg outline-none',
										isSelected
											? isActiveRow
												? 'bg-table-row-selected-hover-bg'
												: 'bg-table-row-selected-bg'
											: isActiveRow && 'bg-table-row-hover-bg',
										variantClass,
										customRowClass,
									)}
									onMouseEnter={() => {
										activeRowMovedByKeyboardRef.current = false
										shiftExtendAnchorRef.current = null
										lastRowIndexRef.current = virtualItem.index
										setActiveRowIndex(virtualItem.index)
										if (enableGrouping && row.getIsGrouped?.()) {
											hoveredGroupRowRef.current = row
											setHoveredRow(null)
											return
										}
										hoveredGroupRowRef.current = null
										setHoveredRow(row.original)
									}}
									onMouseLeave={() => {
										hoveredGroupRowRef.current = null
										setHoveredRow(null)
										// Only the mouse's own nudge of the active row index is undone here — a
										// keyboard move since then (activeRowMovedByKeyboardRef) must keep its row
										// active even though the mouse happens to still be leaving this one.
										if (!activeRowMovedByKeyboardRef.current) {
											setActiveRowIndex(-1)
										}
									}}
									onClick={() => {
										// Skip group header rows — single-click navigation is for leaf rows only.
										if (enableGrouping && row.getIsGrouped?.()) {
											return
										}
										onRowClick?.(row.original)
									}}
									onKeyDown={
										onRowClick
											? e => {
													if (e.key === 'Enter' || e.key === ' ') {
														if (enableGrouping && row.getIsGrouped?.()) {
															return
														}
														e.preventDefault()
														onRowClick(row.original)
													}
												}
											: undefined
									}
									onDoubleClick={() => onRowDoubleClick?.(row.original)}
									onContextMenu={e => {
										// Always prevent browser context menu on table rows
										e.preventDefault()
										// Compute the row ID - use TanStack's row.id for group rows, computed ID for leaf rows
										const isGroupRow = enableGrouping && row.getIsGrouped?.()
										const rowId = isGroupRow ? row.id : getRowId ? getRowId(row.original) : row.id
										// If selection is enabled and row not selected, select it (skip for group rows
										// and rows rejected by selectableRowFilter — their checkbox is disabled, so
										// right-click must not select them either)
										if (
											!isGroupRow &&
											enableSelection &&
											selectionState &&
											!isSelected &&
											selectionState.isRowSelectable(row.original)
										) {
											selectionState.replaceSelection(rowId, row.original)
										}
										// Call the callback for additional handling (e.g., showing contextual menu)
										onRowRightClick?.(row.original, e, rowId)
									}}>
									{enableGrouping && row.getIsGrouped() ? (
										// Full-width group row rendering - Span entire row width
										<>
											{cells.map(cell => {
												// ONLY render the grouped column cell.
												// We skip the selection column here because the group header
												// (StatusGroupHeader) usually contains its own selection logic.
												const isGroupedCell = cell.getIsGrouped()

												if (!isGroupedCell) {
													return null
												}

												// Sized to the viewport, not the scroll content: a sticky cell as wide as
												// the content has nowhere to stick and scrolls with the row.
												return (
													<div
														key={cell.id}
														className={cn(
															'flex items-center text-body shrink-0 relative px-0',
															getFrozenCellClass(isSelected, isActiveRow),
														)}
														style={{
															position: 'sticky',
															left: 0,
															zIndex: 10,
															width: shouldExpandColumns ? '100%' : `${viewportWidth}px`,
														}}>
														<div className='flex items-center w-full'>
															{flexRender(cell.column.columnDef.cell, {
																...cell.getContext(),
																virtualIndex: row.index,
															})}
														</div>
													</div>
												)
											})}
										</>
									) : (
										// Standard row rendering
										cells.map((cell, index) => {
											const isFrozenLeft = frozenColumnSet.has(cell.column.id)
											const isFrozenRight = frozenRightColumnSet.has(cell.column.id)
											const isFrozen = isFrozenLeft || isFrozenRight
											const leftOffset = getFrozenLeftOffset(index, columnWidths)
											const rightOffset = getFrozenRightOffset(index, columnWidths)
											const meta = cell.column.columnDef.meta as ColumnMeta | undefined
											const cellAlignment = meta?.alignment || 'left'
											// Check if this is the first right-frozen cell to add margin-left: auto
											const isFirstRightFrozen =
												isFrozenRight && (index === 0 || !frozenRightColumnSet.has(cells[index - 1]?.column.id))
											// The row's title: its first real column, after the checkbox if there is one.
											const isTitleCell =
												cell.column.id !== 'select' && cells.slice(0, index).every(c => c.column.id === 'select')

											return (
												<div
													key={cell.id}
													data-column-id={cell.column.id}
													data-testid={dataTestId ? `${dataTestId}-cell-${cell.column.id}-${rowKey}` : undefined}
													className={cn(
														'flex items-center text-body shrink-0 relative',
														cell.column.id === 'select' ? 'justify-center px-0' : TABLE_CELL_PADDING_CLASS,
														isFrozen ? getFrozenCellClass(isSelected, isActiveRow) : '',
														index === cells.length - 1 && 'flex-1',
														// !: the title is often a custom-rendered link with its own font-weight class,
														// which would otherwise win over an inherited one from this wrapper.
														isTitleCell && isActiveRow && 'font-medium! [&_*]:font-medium!',
													)}
													{...(cell.column.id === 'select' && { 'data-selection-safe-zone': 'true' })}
													style={{
														width: `${columnWidths[index]}px`,
														...(isFirstRightFrozen && {
															marginLeft: 'auto',
														}),
														...(isFrozenLeft && {
															position: 'sticky',
															left: `${leftOffset}px`,
															zIndex: 10,
														}),
														...(isFrozenRight && {
															position: 'sticky',
															right: `${rightOffset}px`,
															zIndex: 10,
														}),
													}}>
													{enableSelection && cell.column.id === 'select' && (
														<div
															data-row-checkbox='true'
															data-testid={dataTestId ? `${dataTestId}-row-checkbox-cell-${rowKey}` : undefined}
															className={cn(
																'flex items-center justify-center size-full transition-opacity duration-200',
																isMobile
																	? 'opacity-100'
																	: isSelected || headerHovered
																		? 'opacity-100'
																		: 'opacity-0 group-hover:opacity-100 focus-within:opacity-100',
															)}>
															{flexRender(cell.column.columnDef.cell, {
																...cell.getContext(),
																virtualIndex: row.index,
															})}
														</div>
													)}

													{cell.column.id !== 'select' &&
														(meta?.disableTruncation ? (
															<div
																className={cn(
																	cellAlignment === 'center' && 'justify-center text-center',
																	cellAlignment === 'right' && 'justify-end text-right',
																	'flex items-center w-full',
																)}>
																{flexRender(cell.column.columnDef.cell, {
																	...cell.getContext(),
																	virtualIndex: row.index,
																})}
															</div>
														) : (
															<TruncatedCell align={cellAlignment}>
																{flexRender(cell.column.columnDef.cell, {
																	...cell.getContext(),
																	virtualIndex: row.index,
																})}
															</TruncatedCell>
														))}
												</div>
											)
										})
									)}
									{!(enableGrouping && row.getIsGrouped?.()) &&
										isActiveRow &&
										((enableSelection && selectionState && selectionState.isRowSelectable(row.original)) ||
											onRowDoubleClick) && (
											<div
												aria-hidden='true'
												className='pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-3 rounded-md bg-card/90 px-2 py-1 shadow-sm select-none'>
												{enableSelection && selectionState && selectionState.isRowSelectable(row.original) && (
													<ShortcutHint label={isSelected ? 'Deselect' : 'Select'} keys='X' surface='content' />
												)}
												{onRowDoubleClick && <ShortcutHint label='Open' keys='Space' surface='content' />}
											</div>
										)}
								</div>
							</div>
						)
					})}
				</div>
			</div>
			{/* Selection Toolbar: single portal outside row loop so only one menu is ever rendered */}
			{selectionToolbarProps &&
				selectionState &&
				selectionState.selectedCount > 0 &&
				fixedToolbarPosition &&
				!isDialogOpen &&
				(preserveSelectionToolbarInBottomBar ? isBottomBarOpen : !isBottomBarOpen) &&
				createPortal(
					<div
						data-testid={
							selectionToolbarProps.dataTestId ? `${selectionToolbarProps.dataTestId}-contextual-menu` : undefined
						}
						// z-110 clears the dialog layer (z-100) for a table rendered inside one. A page table's
						// toolbar is hidden while a dialog is open, so nothing else rides that high.
						className='fixed z-110 flex items-center bg-card border border-border shadow-levitating rounded-md pointer-events-auto h-(--height-contextual-menu) selection-toolbar-portal'
						style={{
							left: `${fixedToolbarPosition.x - toolbarPortalOrigin.x}px`,
							top: `${fixedToolbarPosition.y - toolbarPortalOrigin.y}px`,
							maxWidth: 'calc(100vw - 20px)',
						}}>
						<SelectionToolbar
							{...selectionToolbarProps}
							preserveSelectionToolbarInBottomBar={preserveSelectionToolbarInBottomBar}
							tableContainerRef={tableContainerRef}
							selectedCount={selectionState.selectedCount}
							className={cn(
								selectionToolbarProps?.anchorToFirstColumn ? '' : 'whitespace-nowrap',
								'text-body',
								'left-0! top-0!',
							)}
							style={{ position: 'relative', transform: 'none', ...selectionToolbarProps?.style }}
							onClearSelection={() => {
								// Clear the table's internal selection so the toolbar hides; then notify parent.
								selectionState.deselectAll()
								selectionToolbarProps?.onClearSelection?.()
							}}
						/>
					</div>,
					toolbarPortalHost ?? document.body,
				)}
		</div>
	)

	// Conditional DndContext wrapper
	if (!enableColumnReordering) {
		return tableContent
	}

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			modifiers={[restrictToHorizontalAxis]}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
			onDragCancel={handleDragCancel}>
			{tableContent}

			<DragOverlay
				dropAnimation={{
					duration: 200,
					easing: 'ease-out',
				}}>
				{activeColumnId && isDragActive ? (
					<div className='drag-overlay bg-background border shadow-lg rounded px-4 py-2 font-medium text-body opacity-90'>
						<div className='flex items-center gap-icon-text'>
							<GripVertical className='size-(--icon-standalone) text-muted-foreground' />
							{activeColumnId}
						</div>
					</div>
				) : null}
			</DragOverlay>
		</DndContext>
	)
}
